import {
  assertSafeIntegerInRange,
  CANNON_CATALOG,
  CANNON_RARITIES,
  type CannonRarity,
} from '@kenocyber/shared';
import { MAX_CANNON_LEVEL } from './merge';

/**
 * Cannons.
 *
 * A cannon NEVER increases the damage of an individual projectile. The level is
 * the number of visible, simultaneously firing cannons; the rarity decides how
 * many projectiles each cannon emits per shot.
 *
 * ## Rarity is restricted
 *
 * Cannons exist **only** as Excellent, Perfect and Anomaly. Common, Uncommon,
 * Rare, Epic, Mythic and Legendary cannons do not exist — this is a closed
 * design decision, not a pending value. Neither does a Prismatic one: it was a
 * planned fourth cannon and that update was cancelled (ADR 0132). The functions
 * here therefore take {@link CannonRarity}, never the general ten-tier
 * `Rarity`.
 *
 * Everything here is a *count*. The server resolves combat from fire cycles and
 * deterministic hit counts, never from how many sprites the client drew - see
 * `docs/ARCHITECTURE.md`.
 */

export type { CannonRarity };
export { CANNON_RARITIES, MAX_CANNON_LEVEL };

/** Level `L` means `L` cannons are visible and firing. */
export function cannonCountForLevel(level: number): number {
  return assertSafeIntegerInRange(level, 1, MAX_CANNON_LEVEL, 'cannon level');
}

/**
 * **O nível com que um item GUARDADO conta hoje** (gate V28I.CH.11A, ADR 0138).
 *
 * O teto desceu de 7 para 5, e uma instância guardada acima dele **não se
 * apaga**: a linha fica onde está, e o que muda é o teto por que ela é lida.
 * Seis e sete contam como cinco; de um a cinco nada muda.
 *
 * **Porque é uma função à parte, e não um `min` dentro de
 * {@link cannonCountForLevel}.** Aquela é estrita de propósito: um nível acima
 * do teto a chegar de DENTRO do jogo é um defeito, e saneá-lo em silêncio
 * esconderia o defeito. Esta é a fronteira da PERSISTÊNCIA, e só quem lê uma
 * linha antiga a atravessa.
 *
 * Nunca sobe um nível: um item de nível 3 continua a contar 3. O que ela faz é
 * um teto, e um teto não é um reembolso nem uma promoção.
 */
export function effectiveCannonLevel(storedLevel: number): number {
  return Math.min(
    assertSafeIntegerInRange(storedLevel, 1, Number.MAX_SAFE_INTEGER, 'stored cannon level'),
    MAX_CANNON_LEVEL,
  );
}

/**
 * Projectiles emitted per cannon, per shot.
 *
 * Complete: every cannon rarity that exists has a decided value. There is no
 * `null` case, because there is no cannon rarity left undecided. Derived from
 * the shared catalog so the number lives in exactly one place.
 */
export const CANNON_PROJECTILES_PER_CANNON: Readonly<Record<CannonRarity, number>> = Object.freeze(
  Object.fromEntries(
    CANNON_RARITIES.map((rarity) => [rarity, CANNON_CATALOG[rarity].projectilesPerCannon]),
  ) as Record<CannonRarity, number>,
);

/** Neon colour of the projectiles emitted by a cannon of this rarity. */
export const CANNON_PROJECTILE_PAINT: Readonly<Record<CannonRarity, readonly string[]>> =
  Object.freeze({
    excellent: ['#2F9BFF'], // neon blue
    perfect: ['#FFE81F'], // neon yellow
    anomaly: ['#FF2222'], // neon red
  });

/** Total projectiles released by one full volley: cannons x projectiles each. */
export function projectilesPerVolley(rarity: CannonRarity, level: number): number {
  return cannonCountForLevel(level) * CANNON_PROJECTILES_PER_CANNON[rarity];
}

export interface CannonSlotOffset {
  /** Offset from the ship centre, in ship-widths. */
  readonly x: number;
  readonly y: number;
}

/**
 * Triangular formation around the ship.
 *
 * Cannons sit on an upward-pointing triangle centred on the ship and are pushed
 * outwards far enough to never cover the ship art. `spread` is a pure visual
 * constant and is expressed in ship-widths so it survives any resolution.
 *
 * This is deterministic geometry, which is why it lives in the domain package
 * and can be unit-tested without a renderer.
 */
export function cannonFormationOffsets(level: number, spread = 0.9): readonly CannonSlotOffset[] {
  const count = cannonCountForLevel(level);
  const offsets: CannonSlotOffset[] = [];
  for (let i = 0; i < count; i += 1) {
    // Distribute around a triangle: -90deg is straight up, then 120deg steps,
    // with each full lap pushed one ring further out.
    const ring = Math.floor(i / 3);
    const corner = i % 3;
    const angle = -Math.PI / 2 + corner * ((2 * Math.PI) / 3);
    const radius = spread * (1 + ring * 0.55);
    offsets.push({
      x: Number((Math.cos(angle) * radius).toFixed(4)),
      y: Number((Math.sin(angle) * radius).toFixed(4)),
    });
  }
  return offsets;
}

/**
 * There is deliberately NO attack-speed threshold that changes the visual.
 *
 * An earlier build let the renderer replace the sprites with a continuous beam
 * above 30 volleys per second. Artifacts make that cadence ordinary - Speed
 * Fast at level 10 is +30.72 on its own - and KENOCYBER never converts shots
 * into lasers, so the threshold is gone rather than raised. High cadence is
 * drawn as more discrete projectiles, each with its own damage number.
 *
 * @see CLAUDE.md, "Projectiles are always discrete"
 */
